import type { FeatureFlagCatalog } from "@cede/types";
import { useInternalFeatureFlags } from "./useInternalFeatureFlags";
import { useAuthenticationProvider } from "@/hooks/authentication/useAuthenticationProvider";
import { useHubUser } from "@/hooks/user/useHubUser";

/**
 * Check if the current user has access to a feature.
 *
 * Semantics:
 * - `enabled=false` → nobody
 * - user in `forbiddenHubUsers` → nobody (deny overrides any allow below);
 *   deny authenticated users while their hub user id is still loading (fail closed)
 * - `enabled=true`, no whitelist → everyone
 * - `enabled=true`, whitelist="0xA,0xB" → only those addresses
 */
export const useFeatureAccess = <K extends keyof FeatureFlagCatalog>(featureKey: K): boolean => {
  const { data: internalFlags } = useInternalFeatureFlags();
  const { user, authenticated } = useAuthenticationProvider();
  const { hubUser } = useHubUser();

  const entry = internalFlags?.[featureKey];
  if (!entry || typeof entry.value !== "boolean" || !entry.value) return false;

  const forbiddenHubUsers = entry.forbiddenHubUsers ?? [];
  if (forbiddenHubUsers.length > 0) {
    // Fail closed: an authenticated user whose hub id hasn't resolved could be
    // on the list — deny until we can actually check it.
    if (authenticated && hubUser?.id == null) return false;
    if (hubUser?.id != null && forbiddenHubUsers.includes(String(hubUser.id))) return false;
  }

  const whitelist = entry.whitelistedAddresses;
  if (!whitelist) return true; // No whitelist → everyone

  const userAddress = user?.wallet?.address;
  if (!userAddress) return false;
  const allowed = whitelist.split(",").map((a) => a.trim().toLowerCase());
  return allowed.includes(userAddress.toLowerCase());
};
